import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../../lib/api/client';
import { useCart } from './cart';
import { useAccount } from '../../lib/api/hooks';
import { AddressFields, addressComplete, emptyAddress, type Address } from '../account/address';
import { estimateDiscountCents, promoBlockedReason, type AppliedPromo } from '../../lib/store/promo';

const money = (c: number) => `$${(c / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;

interface CanceledItem { productId: string; name: string; priceCents: number; quantity: number }
interface RateOption { serviceType: string; serviceName: string; amountCents: number; currency: string; transitDays?: number | null }
interface AddressValidation {
  resolved: boolean;
  unavailable?: boolean;
  classification?: string;
  normalized?: { line1: string; city: string; state: string; postalCode: string; country: string };
  messages?: string[];
}

function addrDiffers(n: AddressValidation['normalized'], a: Address): boolean {
  if (!n) return false;
  const norm: Record<string, string> = { line1: n.line1, city: n.city, state: n.state, postalCode: n.postalCode };
  const cur = a as unknown as Record<string, string>;
  const norm8 = (s: string | undefined) => (s ?? '').trim().toUpperCase();
  return Object.keys(norm).some((k) => norm8(norm[k]) !== norm8(cur[k]));
}

export function CartPage() {
  const cart = useCart();
  const account = useAccount();
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [params, setParams] = useSearchParams();
  const handledCancel = useRef(false);

  const [addr, setAddr] = useState<Address>(emptyAddress());
  const [prefilled, setPrefilled] = useState(false);
  const [rates, setRates] = useState<RateOption[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [ratesBusy, setRatesBusy] = useState(false);
  const [busy, setBusy] = useState(false);
  const [suggestion, setSuggestion] = useState<Address | null>(null);
  const [addrWarn, setAddrWarn] = useState<string | null>(null);
  const [showAddrErrors, setShowAddrErrors] = useState(false);

  // Prefill the ship-to from the account's physical (or billing) address, once.
  useEffect(() => {
    if (prefilled) return;
    const a = account.data?.physicalAddress ?? account.data?.billingAddress;
    if (a || account.data?.name) {
      // Prefill recipient name from the account name unless the saved address already carries one.
      setAddr({ ...emptyAddress(), ...(a ?? {}), name: a?.name || account.data?.name || '' });
      setPrefilled(true);
    }
  }, [account.data, prefilled]);

  // Returning from a canceled Stripe checkout: discard the pending order and restore its items to the cart.
  useEffect(() => {
    const canceled = params.get('canceled');
    if (!canceled || handledCancel.current) return;
    handledCancel.current = true;
    (async () => {
      try {
        const res = await api.post<{ canceled: boolean; items: CanceledItem[] }>(`/orders/${canceled}/cancel`);
        for (const it of res.items) {
          cart.add({ productId: it.productId, name: it.name, priceCents: it.priceCents }, it.quantity);
        }
        if (res.items.length) setNotice('Checkout canceled — your items are back in your cart.');
      } catch {
        setNotice('Checkout was canceled.');
      } finally {
        params.delete('canceled');
        setParams(params, { replace: true });
      }
    })();
  }, [params, cart, setParams]);

  // Any address change invalidates prior rates + validation state.
  const onAddr = (a: Address) => { setAddr(a); setRates(null); setSelected(null); setSuggestion(null); setAddrWarn(null); };

  // Validate the address with FedEx, then rate. A suggested correction pauses for the customer to
  // accept/keep; an unverifiable address warns but still proceeds (non-blocking).
  const validateThenRate = async () => {
    setRatesBusy(true); setError(null); setSuggestion(null); setAddrWarn(null);
    try {
      const v = await api.post<AddressValidation>('/orders/validate-address', { address: addr });
      if (v.normalized && addrDiffers(v.normalized, addr)) {
        /*
         * Overlay the normalised fields onto what the customer entered — do NOT rebuild from
         * emptyAddress(). FedEx returns only line1/city/state/postalCode/country, so starting from an
         * empty address silently dropped the recipient name and phone: accepting "Use this address"
         * wiped both, and the vendor PO went out with no-one to ship to.
         */
        setSuggestion({ ...addr, ...v.normalized });
        setRatesBusy(false);
        return; // wait for Use/Keep
      }
      if (!v.resolved && !v.unavailable) setAddrWarn(v.messages?.[0] || "We couldn't verify this address — please double-check it before ordering.");
    } catch {
      // validation unavailable — don't block the sale
    }
    await getRates(addr);
  };

  const getRates = async (useAddr: Address = addr) => {
    setRatesBusy(true); setError(null); setRates(null); setSelected(null);
    try {
      const res = await api.post<{ options: RateOption[]; fallbackUsed: boolean }>('/orders/shipping-rates', {
        items: cart.lines.map((l) => ({ productId: l.productId, quantity: l.quantity })),
        destination: { postalCode: useAddr.postalCode, state: useAddr.state, countryCode: useAddr.country },
      });
      setRates(res.options);
      if (res.options[0]) setSelected(res.options[0].serviceType); // default to cheapest
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not get shipping rates');
    } finally {
      setRatesBusy(false);
    }
  };

  /*
   * The applied promotion. `null` until the customer applies one; a rejected code is kept as an object
   * with `valid: false` so its reason stays on screen beside the field.
   */
  const [promoInput, setPromoInput] = useState('');
  const [promo, setPromo] = useState<AppliedPromo | null>(null);
  const [promoBusy, setPromoBusy] = useState(false);

  const applyPromo = async () => {
    const code = promoInput.trim();
    if (!code) return;
    setPromoBusy(true);
    try {
      setPromo(await api.post<AppliedPromo>('/orders/promo', { code }));
    } catch (e) {
      // A transport failure is not a bad code, and must not be reported to the customer as one.
      setPromo({ valid: false, reason: e instanceof ApiError ? e.message : 'Could not check that code.' });
    } finally {
      setPromoBusy(false);
    }
  };

  const checkout = async () => {
    setBusy(true); setError(null);
    try {
      const res = await api.post<{ checkoutUrl: string }>('/orders/checkout', {
        items: cart.lines.map((l) => ({ productId: l.productId, quantity: l.quantity })),
        // Send the full shown quote so a transient FedEx outage at checkout keeps the chosen service.
        shipping: chosen
          ? { serviceType: chosen.serviceType, serviceName: chosen.serviceName, amountCents: chosen.amountCents }
          : { serviceType: selected ?? 'FLAT' },
        shippingAddress: addr,
        // Re-checked server-side before the session is created — the cart's check is for display only.
        ...(promo?.valid && promo.code ? { promoCode: promo.code } : {}),
      });
      if (res.checkoutUrl) {
        cart.clear();
        window.location.href = res.checkoutUrl; // redirect to Stripe only at checkout
      } else {
        setError('Could not start checkout.');
      }
    } catch (e) {
      const msg = e instanceof ApiError ? e.message : 'Checkout failed';
      /*
       * Stripe can refuse a discount only at session creation — a coupon can read as valid on every object
       * we are able to inspect and still be rejected for the cart's items. When that is why checkout failed
       * the message belongs beside the code, next to the thing to remove.
       *
       * EITHER place, never both. Setting `error` as well printed the same sentence twice, once under the
       * code and once under the total, which reads as two separate faults.
       */
      if (promo?.valid && /promotion code/i.test(msg)) {
        setPromo({ valid: false, reason: msg });
        setPromoInput(promo.code ?? '');
        setError(null);
      } else {
        setError(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const chosen = rates?.find((r) => r.serviceType === selected) ?? null;
  /*
   * An ESTIMATE, applied to items only — Stripe discounts eligible line items, not the shipping rate, and
   * the order is reconciled from the Checkout Session after payment. It is deliberately never larger than
   * what Stripe will take off, so the total shown is never lower than the total charged.
   */
  const discount = estimateDiscountCents(promo, cart.subtotalCents);
  const promoNote = promoBlockedReason(promo, cart.subtotalCents);
  const total = cart.subtotalCents - discount + (chosen?.amountCents ?? 0);

  return (
    <div>
      <div className="console-head">
        <div>
          <h1>Cart</h1>
          <div className="sub">Review your items, choose shipping, then check out securely with Stripe.</div>
        </div>
      </div>

      {notice && (
        <div className="panel" style={{ borderColor: 'color-mix(in srgb, var(--accent) 45%, transparent)', marginBottom: 16 }}>
          {notice}
        </div>
      )}

      {cart.lines.length === 0 ? (
        <div className="panel empty">
          <h3>Your cart is empty</h3>
          <p>Browse the <Link to="/app/store" className="track-link">Store</Link> to add scanners.</p>
        </div>
      ) : (
        <div className="cart-layout">
          <div>
            <div className="panel cart-lines">
              {cart.lines.map((l) => (
                <div className="cart-line" key={l.productId}>
                  <div className="cart-line-info">
                    <span className="cart-line-name">{l.name}</span>
                    <span className="cart-line-unit">{money(l.priceCents)} each</span>
                  </div>
                  <div className="cart-line-actions">
                    <input
                      className="input store-qty"
                      type="number"
                      min={1}
                      value={l.quantity}
                      onChange={(e) => { cart.setQty(l.productId, Math.max(1, Number(e.target.value))); setRates(null); setSelected(null); }}
                    />
                    <span className="cart-line-total mono">{money(l.priceCents * l.quantity)}</span>
                    <button className="cart-remove" aria-label="Remove" onClick={() => { cart.remove(l.productId); setRates(null); setSelected(null); }}>×</button>
                  </div>
                </div>
              ))}
            </div>

            {/* Shipping address + rates */}
            <div className="panel" style={{ marginTop: 16 }}>
              <h3 style={{ marginTop: 0 }}>Shipping address</h3>
              <AddressFields idPrefix="ship" value={addr} onChange={onAddr} showContact showErrors={showAddrErrors} />

              {suggestion && (
                <div className="banner-warn" style={{ marginTop: 14 }}>
                  <div style={{ marginBottom: 8 }}>
                    <strong>Did you mean:</strong> {suggestion.line1}, {suggestion.city} {suggestion.state} {suggestion.postalCode}?
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-primary btn-sm" onClick={() => {
                      const s = suggestion;
                      setSuggestion(null);
                      setAddr(s);
                      // Re-check here too: this path calls getRates directly, so it would otherwise skip
                      // the completeness gate that gates the "See rates" button.
                      if (!addressComplete(s, true)) { setShowAddrErrors(true); return; }
                      getRates(s);
                    }}>Use this address</button>
                    <button className="btn btn-ghost btn-sm" onClick={() => { setSuggestion(null); getRates(addr); }}>Keep mine</button>
                  </div>
                </div>
              )}
              {addrWarn && <div className="banner-warn" style={{ marginTop: 14 }}>⚠ {addrWarn}</div>}

              {/* The primary action until rates exist: Checkout is hidden behind it, so a ghost button
                  left the cart looking like a dead end. Demoted to ghost once rates are in and Checkout
                  below becomes the thing to click — two green buttons would just compete. */}
              <button className={`btn ${rates ? 'btn-ghost' : 'btn-primary'} btn-sm`} style={{ marginTop: 14 }} disabled={ratesBusy}
                onClick={() => {
                  if (!addressComplete(addr, true)) { setShowAddrErrors(true); return; } // highlight what's missing
                  setShowAddrErrors(false);
                  void validateThenRate();
                }}>
                {ratesBusy ? 'Getting rates…' : rates ? 'Refresh rates' : 'Get shipping rates'}
              </button>
              {showAddrErrors && !addressComplete(addr, true) && <span className="flow-hint" style={{ marginLeft: 10, color: 'var(--rose)' }}>Fill the highlighted fields to see rates.</span>}

              {rates && (
                <div className="ship-options" style={{ marginTop: 16 }}>
                  {rates.map((r) => (
                    <label key={r.serviceType} className={`ship-option${selected === r.serviceType ? ' is-selected' : ''}`}>
                      <input type="radio" name="ship" checked={selected === r.serviceType} onChange={() => setSelected(r.serviceType)} />
                      <span className="ship-option-name">
                        {r.serviceName}
                        {r.transitDays ? <span className="muted"> · {r.transitDays} business day{r.transitDays === 1 ? '' : 's'}</span> : null}
                      </span>
                      <span className="ship-option-price mono">{money(r.amountCents)}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          <aside className="panel cart-summary">
            <h3>Order summary</h3>
            <div className="cart-sum-row"><span>Items ({cart.count})</span><span className="mono">{money(cart.subtotalCents)}</span></div>
            {/* Between items and shipping, where the reduction it produces will appear. */}
            {promo?.valid ? (
              <div className="promo-applied">
                <div>
                  <span className="promo-code mono">{promo.code}</span>
                  <span className="promo-label">{promo.label}</span>
                  {promoNote && <div className="promo-note">{promoNote}</div>}
                </div>
                <button
                  type="button" className="chip-x" aria-label="Remove promotion code"
                  onClick={() => { setPromo(null); setPromoInput(''); }}
                >✕</button>
              </div>
            ) : (
              <div className="promo-entry">
                <input
                  type="text" className="promo-input" value={promoInput} aria-label="Promotion code"
                  placeholder="Promo code"
                  onChange={(e) => setPromoInput(e.target.value)}
                  // Enter is what people press in a one-field form; without it this looks broken.
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void applyPromo(); } }}
                />
                <button
                  type="button" className="btn btn-ghost btn-sm"
                  disabled={promoBusy || !promoInput.trim()} onClick={() => void applyPromo()}
                >{promoBusy ? '…' : 'Apply'}</button>
              </div>
            )}
            {promo && !promo.valid && <p className="promo-error">{promo.reason}</p>}
            {discount > 0 && (
              <div className="cart-sum-row promo-discount">
                <span>Discount</span><span className="mono">−{money(discount)}</span>
              </div>
            )}
            <div className="cart-sum-row"><span>Shipping</span><span className="mono">{chosen ? money(chosen.amountCents) : '—'}</span></div>
            <div className="cart-sum-total"><span>Total</span><span className="mono">{money(total)}</span></div>
            {error && <p className="cart-error">{error}</p>}
            {/* Don't show Checkout until shipping has been rated — otherwise it looks like you can
                pay with no shipping cost. Rates auto-select the cheapest, so once fetched it's ready. */}
            {rates ? (
              <>
                <button className="btn btn-primary cart-checkout" onClick={checkout} disabled={busy || !chosen}>
                  {busy ? 'Starting checkout…' : 'Checkout →'}
                </button>
                {!chosen && <p className="cart-note">// choose a shipping option to continue</p>}
              </>
            ) : (
              <p className="cart-note">// get shipping rates above to continue to checkout</p>
            )}
            <p className="cart-note" style={{ marginTop: 8 }}>// you'll be redirected to Stripe to pay securely</p>
            <p className="cart-note" style={{ marginTop: 8 }}>
              By placing your order you agree to our{' '}
              <a href="/legal/terms" target="_blank" rel="noreferrer">Terms</a>,{' '}
              <a href="/legal/privacy" target="_blank" rel="noreferrer">Privacy</a>, and{' '}
              <a href="/legal/returns" target="_blank" rel="noreferrer">Return</a> policies.
            </p>
          </aside>
        </div>
      )}
    </div>
  );
}
